/** Download / share / save-as helpers (docs/research/04 §4.4). */

export function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function canShareFiles(sample?: File[]): boolean {
  try {
    const files = sample ?? [new File([new Uint8Array(1)], 'x.jpg', { type: 'image/jpeg' })];
    return (
      typeof navigator.share === 'function' && typeof navigator.canShare === 'function' && navigator.canShare({ files })
    );
  } catch {
    return false;
  }
}

/** Shares files in one sheet. Returns false when the user dismissed the sheet. */
export async function shareFiles(files: File[]): Promise<boolean> {
  if (!canShareFiles(files)) throw new Error('Sharing is not available on this device.');
  try {
    await navigator.share({ files });
    return true;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return false;
    throw e;
  }
}

type SavePicker = (opts: {
  suggestedName: string;
  types: { description: string; accept: Record<string, string[]> }[];
}) => Promise<FileSystemFileHandle>;

export function hasSavePicker(): boolean {
  return typeof (window as unknown as { showSaveFilePicker?: SavePicker }).showSaveFilePicker === 'function';
}

/** Chromium "Save As…": lets the user pick a folder. Returns false if cancelled. */
export async function saveWithPicker(blob: Blob, name: string): Promise<boolean> {
  const picker = (window as unknown as { showSaveFilePicker: SavePicker }).showSaveFilePicker;
  const ext = name.slice(name.lastIndexOf('.'));
  try {
    const handle = await picker({
      suggestedName: name,
      types: [{ description: blob.type === 'image/png' ? 'PNG image' : 'JPEG image', accept: { [blob.type]: [ext] } }],
    });
    const w = await handle.createWritable();
    await w.write(blob);
    await w.close();
    return true;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return false;
    throw e;
  }
}
